import { memo, useEffect, useRef } from "react";
import CardMediaMUI from "@mui/material/CardMedia";
import { useInView } from "react-intersection-observer";
import Skeleton from "@mui/material/Skeleton";
import { formatUrlMedia, getExtensionFile } from "../../utils/string";
import { VIDEO_FORMATS } from "../../utils/constants";

const NOOP = () => {};

let propsTypeVideo = [
  "controls",
  "autoplay",
  "loop",
  "controlsList",
  "muted",
  "preload",
];
let propsTypeImage = ["draggable", "alt"];

const CardMedia = memo(function CardMedia({
  propsType = {},
  sx = {},
  sxImage = {},
  sxVideo = {},
  url,
  onFailedLoadUrl = () => {},
  onMediaLoad = NOOP,
  onClick,
}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "300px 0px",
  });
  const extension = getExtensionFile(url);
  url = formatUrlMedia(url);
  const type = VIDEO_FORMATS.includes(extension) ? "video" : "img";
  const mediaRef = useRef(null);

  const getPropsType = () => {
    const propsTypeKeysFiltered = Object.keys(propsType).filter((propsType) => {
      if (type === "video" && propsTypeVideo.includes(propsType)) {
        return true;
      }

      if (type === "img" && propsTypeImage.includes(propsType)) {
        return true;
      }

      if (
        !propsTypeImage.includes(propsType) &&
        !propsTypeVideo.includes(propsType)
      ) {
        return true;
      }

      return false;
    });

    const propsTypeData = {};

    propsTypeKeysFiltered.forEach((key) => {
      propsTypeData[key] = propsType[key];
    });

    return propsTypeData;
  };

  const propsTypeFiltered = getPropsType();

  const propsMedia =
    type === "video"
      ? {
          component: type,
          controls: true,
          autoplay: false,
          loop: false,
          preload: "metadata",
          src: url,
          ...propsTypeFiltered,
        }
      : {
          component: type,
          src: url,
          ...propsTypeFiltered,
        };

  useEffect(() => {
    if (type !== "video" || !inView) return;
    const videoElement = mediaRef.current;
    if (!videoElement || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoElement) return;
        if (entry.isIntersecting) {
          if (propsMedia.autoplay) {
            videoElement.play().catch(() => {});
          }
          return;
        }
        videoElement.pause();
      },
      {
        threshold: 0.15,
        rootMargin: "80px 0px",
      }
    );

    observer.observe(videoElement);

    return () => {
      observer.disconnect();
    };
  }, [inView, propsMedia.autoplay, type, url]);

  useEffect(() => {
    if (!inView) return;
    const mediaElement = mediaRef.current;
    if (!mediaElement) return;

    if (
      type === "img" &&
      mediaElement.complete &&
      mediaElement.naturalWidth > 0
    ) {
      onMediaLoad();
      return;
    }

    if (type === "video" && mediaElement.readyState >= 2) {
      onMediaLoad();
    }
  }, [inView, onMediaLoad, type, url]);

  if (!inView) {
    return (
      <Skeleton
        ref={ref}
        variant="rounded"
        sx={{
          width: "100%",
          height: "100%",
        }}
      />
    );
  }

  // if (type === "video" && videoComponent === "player") {
  //   return <VideoPlayer url={url} sx={{ ...sx, ...sxVideo }} {...propsMedia} />;
  // }

  if (type === "video") {
    return (
      <CardMediaMUI
        {...propsMedia}
        ref={mediaRef}
        sx={{ ...sx, ...sxVideo }}
        onLoadedData={onMediaLoad}
        onError={onFailedLoadUrl}
        onClick={onClick}
      />
    );
  }
  return (
    <CardMediaMUI
      {...propsMedia}
      ref={mediaRef}
      sx={{ ...sx, ...sxImage }}
      onLoad={onMediaLoad}
      onError={onFailedLoadUrl}
      onClick={onClick}
    />
  );
});

export default CardMedia;
